/**
 * animationState.ts
 * Resolve Nova's animation state for the current tick based on physics output.
 * Extracted from physicsStep.ts to keep both files under the 300-line limit.
 *
 * DECISION: Transition states (RUN_START, RUN_STOP, LAND) hold for a fixed
 * frame count before falling through to the next logical state. High-
 * priority states (DEAD, HURT, airborne) interrupt transitions.
 *
 * @module animationState
 */

import {
  NOVA_INVINCIBILITY_FRAMES,
  NOVA_HURT_ANIM_FRAMES,
  RUN_START_FRAMES,
  RUN_STOP_FRAMES,
  LAND_FRAMES,
} from '../../constants/gameConstants';
import type { NovaRuntime } from './gameState';
import { dispatch } from './gameEventDispatch';

/**
 * Resolve Nova's animation state for the current physics tick.
 * Mutates `nova.state`, `nova.animTransitionFrames`, and `nova.prevGrounded`.
 * @param nova - Mutable Nova entity (post-collision).
 * @param isCrouching - Whether the crouch conditions are met this frame.
 */
export function resolveAnimationState(nova: NovaRuntime, isCrouching: boolean): void {
  const prevState = nova.state;
  const justLanded = !nova.prevGrounded && nova.isGrounded;

  if (nova.invincibilityFrames > 0 && nova.hurtFlash &&
      nova.invincibilityFrames > NOVA_INVINCIBILITY_FRAMES - NOVA_HURT_ANIM_FRAMES) {
    nova.state = 'HURT';
    nova.animTransitionFrames = 0;
  } else if (nova.isStomping) {
    nova.state = 'STOMP';
    nova.animTransitionFrames = 0;
  } else if (!nova.isGrounded && nova.vy < 0) {
    nova.state = 'JUMP';
    nova.animTransitionFrames = 0;
  } else if (!nova.isGrounded && nova.vy >= 0) {
    nova.state = 'FALL';
    nova.animTransitionFrames = 0;
  } else if (nova.animTransitionFrames > 0) {
    // Hold current transition state, but allow interrupts
    nova.animTransitionFrames--;
    if (!nova.isGrounded) {
      // Left the ground mid-transition — cancel
      nova.animTransitionFrames = 0;
      nova.state = nova.vy < 0 ? 'JUMP' : 'FALL';
    } else if (isCrouching) {
      // DECISION: Crouch interrupts transitions for immediate feedback
      nova.animTransitionFrames = 0;
      nova.state = 'CROUCH';
    } else if (nova.state === 'RUN_STOP' && Math.abs(nova.vx) > 1) {
      // Re-started moving during stop animation — skip back to RUN
      nova.animTransitionFrames = 0;
      nova.state = 'RUN';
    }
    // Otherwise keep the current transition state unchanged
  } else if (justLanded) {
    nova.state = 'LAND';
    nova.animTransitionFrames = LAND_FRAMES;
    dispatch.land();
  } else if (isCrouching) {
    // DECISION: Crouch takes priority over RUN so that holding down while
    // moving shows the CROUCH state (speed is already capped to CROUCH_MAX_SPEED).
    nova.state = 'CROUCH';
    nova.animTransitionFrames = 0;
  } else if (Math.abs(nova.vx) > 1 && (prevState === 'IDLE' || prevState === 'LAND')) {
    nova.state = 'RUN_START';
    nova.animTransitionFrames = RUN_START_FRAMES;
  } else if (Math.abs(nova.vx) > 1) {
    nova.state = 'RUN';
  } else if (Math.abs(nova.vx) <= 1 && prevState === 'RUN') {
    nova.state = 'RUN_STOP';
    nova.animTransitionFrames = RUN_STOP_FRAMES;
  } else {
    nova.state = 'IDLE';
  }

  nova.prevGrounded = nova.isGrounded;
}
